import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Sparkles, Loader2, Send, CheckCircle2, Zap, Bot, User, ArrowRight } from 'lucide-react';
import { useGenerateWorkflow } from '../helpers/useWorkflowsApi';
import { useAiModels } from '../helpers/useAiModels';
import { Button } from './Button';
import { Textarea } from './Textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';
import { toast } from 'sonner';
import styles from './WorkflowGenerateDialog.module.css';

interface WorkflowGenerateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId?: string;
  onSuccess?: (workflowId: string, n8nUrl?: string) => void;
  /** When set, AI will add nodes to the existing workflow instead of creating a new one */
  existingWorkflow?: {
    id: string;
    name: string;
    nodes: Array<{ name: string; type: string }>;
  } | null;
}

type ChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

const EXAMPLE_PROMPTS = [
  'I want to sell AI-generated prompts on PromptBase automatically',
  'Monitor competitor prices and alert me on Slack when they change',
  'Scrape job listings daily and apply with my resume automatically',
  'Generate social media content and post it across all platforms',
  'When I get an email with an attachment, process it with AI and file it',
];

const INTERVIEW_SYSTEM_PROMPT = `You are an expert workflow architect for Tasking.tech. The user wants to create an automation workflow. Your job is to figure it out with as little back-and-forth as possible, then create the perfect workflow.

Available node types you can use:
- TRIGGERS: webhook, schedule (cron), manual
- BROWSER: navigate, click, type_text, extract_data, screenshot, upload_file, wait_for, scroll, select_option, run_script
- AI: prompt (text generation), image_generate, image_edit, classify, extract_structured_data
- DATA: transform, store_variable, merge, filter
- ACTIONS: http_request, run_code, slack_message, discord_message
- TASKINGBOT: execute (delegate complex task), create_task
- LOGIC: if/else, switch, loop, delay, retry, error_handler

Do NOT run an interview by default. Infer sensible defaults, complete missing details, and ask at most ONE short blocker question only if it is truly impossible to proceed safely.

Assume:
- credentials should come from saved integrations first
- browser work should use the extension when helpful
- desktop work can use the desktop agent when needed
- installed skills can be used via TaskingBot steps when they fit better than built-in nodes

After you have enough information, respond with EXACTLY this format:
[WORKFLOW_READY]
Based on our conversation, here's the workflow I'll create:
<description of the workflow in plain language>
[/WORKFLOW_READY]

Do NOT generate JSON. Just describe the plan clearly. The system will convert it to a workflow.
IMPORTANT: Be conversational and helpful, not robotic. Default to solving, not questioning.`;

const OLLAMA_CLOUD_MODELS = [
  { value: 'qwen3-coder-next:cloud', label: 'Qwen3 Coder Next', provider: 'ollama_cloud' },
  { value: 'glm-5:cloud', label: 'GLM-5', provider: 'ollama_cloud' },
  { value: 'deepseek-v3.2:cloud', label: 'DeepSeek V3.2', provider: 'ollama_cloud' },
  { value: 'llama4-scout:cloud', label: 'Llama 4 Scout', provider: 'ollama_cloud' },
];

export const WorkflowGenerateDialog: React.FC<WorkflowGenerateDialogProps> = ({
  open,
  onOpenChange,
  sessionId,
  onSuccess,
  existingWorkflow,
}) => {
  const [mode, setMode] = useState<'choose' | 'quick' | 'interview'>('choose');
  const [prompt, setPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState('qwen3-coder-next:cloud');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [generatedResult, setGeneratedResult] = useState<{
    id: string;
    name: string;
    n8nUrl?: string;
  } | null>(null);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const generateMutation = useGenerateWorkflow();
  const { data: modelsData } = useAiModels({ enabled: open });

  const availableModels = useMemo(() => {
    const apiModels = (modelsData?.cloudModels || [])
      .filter((m) => m.provider === 'ollama_cloud')
      .map((m) => ({ value: m.model, label: m.name, provider: m.provider }));
    const seen = new Set(apiModels.map((m) => m.value));
    const merged = [...apiModels];
    for (const known of OLLAMA_CLOUD_MODELS) {
      if (!seen.has(known.value)) {
        merged.push(known);
        seen.add(known.value);
      }
    }
    return merged.length > 0 ? merged : OLLAMA_CLOUD_MODELS;
  }, [modelsData]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleGenerate = async (finalPrompt: string) => {
    try {
      const result = await generateMutation.mutateAsync({
        prompt: finalPrompt,
        sessionId,
        model: selectedModel,
      });
      await new Promise((resolve) => setTimeout(resolve, 500));
      setGeneratedResult({
        id: result.workflow.id,
        name: result.workflow.name,
        n8nUrl: result.workflow.n8n?.url,
      });
      toast.success('Workflow generated successfully!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to generate workflow');
    }
  };

  const handleQuickGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please describe what you want the workflow to do');
      return;
    }
    let finalPrompt = prompt.trim();
    if (existingWorkflow) {
      const existingDesc = existingWorkflow.nodes.map((n) => `- ${n.name} (${n.type})`).join('\n');
      finalPrompt = `EXISTING WORKFLOW "${existingWorkflow.name}" already has these nodes:\n${existingDesc}\n\nADD the following to this workflow (keep all existing nodes and connections, add new ones):\n${finalPrompt}`;
    }
    await handleGenerate(finalPrompt);
  };

  const handleStartInterview = () => {
    setMode('interview');
    setChatMessages([
      {
        role: 'assistant',
        content:
          "Hey! I'll help you build the perfect workflow. Tell me — what are you trying to automate? What's the end goal?\n\nFor example: \"I want to generate AI images and sell them on PromptBase\" or \"Monitor a website for changes and alert me\"",
      },
    ]);
  };

  const handleChatSend = async () => {
    const msg = chatInput.trim();
    if (!msg || isThinking) return;

    const newMessages: ChatMessage[] = [...chatMessages, { role: 'user', content: msg }];
    setChatMessages(newMessages);
    setChatInput('');
    setIsThinking(true);

    try {
      // Build conversation for AI
      const apiMessages = [
        { role: 'system', content: INTERVIEW_SYSTEM_PROMPT },
        ...newMessages.map((m) => ({ role: m.role, content: m.content })),
      ];

      const OLLAMA_KEY = '__OLLAMA_CLOUD_KEY__'; // Replaced at runtime by endpoint
      const response = await fetch('/api/n8n/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: `[INTERVIEW_MODE]\n${JSON.stringify(apiMessages)}`,
          sessionId,
          model: selectedModel,
          interviewMode: true,
        }),
      });

      if (!response.ok) {
        // Fallback: use simple AI response simulation
        const aiReply = generateInterviewResponse(newMessages);
        setChatMessages([...newMessages, { role: 'assistant', content: aiReply }]);

        // Check if workflow is ready
        if (aiReply.includes('[WORKFLOW_READY]')) {
          const planMatch = aiReply.match(/\[WORKFLOW_READY\]([\s\S]*?)\[\/WORKFLOW_READY\]/);
          if (planMatch) {
            // Compile full conversation into a generation prompt
            const fullPrompt = compileInterviewToPrompt(newMessages, planMatch[1]);
            await handleGenerate(fullPrompt);
          }
        }
      } else {
        const data = await response.json();
        const aiContent = data.reply || data.workflow?.description || 'I need a bit more information. Can you tell me more about what you want to automate?';

        setChatMessages([...newMessages, { role: 'assistant', content: aiContent }]);

        // Check if the AI says workflow is ready
        if (aiContent.includes('[WORKFLOW_READY]') || data.workflowReady) {
          const fullPrompt = compileInterviewToPrompt(newMessages, aiContent);
          await handleGenerate(fullPrompt);
        }
      }
    } catch (err) {
      // Fallback interview logic
      const aiReply = generateInterviewResponse(newMessages);
      setChatMessages([...newMessages, { role: 'assistant', content: aiReply }]);
    } finally {
      setIsThinking(false);
    }
  };

  const handleClose = () => {
    setPrompt('');
    setMode('choose');
    setChatMessages([]);
    setChatInput('');
    setGeneratedResult(null);
    setIsThinking(false);
    onOpenChange(false);
  };

  const handleOpenWorkflow = () => {
    if (generatedResult) {
      onSuccess?.(generatedResult.id, generatedResult.n8nUrl);
      handleClose();
    }
  };

  // ─── RENDER ─────────────────────────────────────────────

  if (generatedResult) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={styles.dialog}>
          <div className={styles.successContent}>
            <div className={styles.successIcon}>
              <CheckCircle2 size={48} />
            </div>
            <h3 className={styles.successTitle}>Workflow Created!</h3>
            <p className={styles.successName}>{generatedResult.name}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>Close</Button>
            <Button onClick={handleOpenWorkflow}>Open Workflow</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${styles.dialog} ${mode === 'interview' ? styles.dialogWide : ''}`}>
        <DialogHeader>
          <DialogTitle className={styles.title}>
            <Sparkles className={styles.titleIcon} />
            {existingWorkflow ? 'Add Nodes to Workflow' : mode === 'choose' ? 'Create Workflow' : mode === 'quick' ? 'Quick Generate' : 'Workflow Planner'}
          </DialogTitle>
          {mode === 'choose' && (
            <DialogDescription>
              Build a workflow manually, describe it for quick generation, or let AI quickly plan the automation with minimal follow-up.
            </DialogDescription>
          )}
        </DialogHeader>

        {mode === 'choose' && (
          <div className={styles.content}>
            <div className={styles.modeCards}>
              <button className={styles.modeCard} onClick={() => setMode('quick')}>
                <Zap size={24} className={styles.modeIconQuick} />
                <div>
                  <strong>Quick Generate</strong>
                  <p>Describe what you want in one shot</p>
                </div>
                <ArrowRight size={16} className={styles.modeArrow} />
              </button>
              <button className={styles.modeCard} onClick={handleStartInterview}>
                <Bot size={24} className={styles.modeIconInterview} />
                <div>
                  <strong>AI Planner</strong>
                  <p>AI plans the workflow and only asks if there is a real blocker</p>
                </div>
                <ArrowRight size={16} className={styles.modeArrow} />
              </button>
            </div>

            <div className={styles.examples}>
              <p className={styles.examplesLabel}>Popular automations:</p>
              <div className={styles.examplesList}>
                {EXAMPLE_PROMPTS.map((example, i) => (
                  <button
                    key={i}
                    className={styles.exampleButton}
                    onClick={() => {
                      setPrompt(example);
                      setMode('quick');
                    }}
                  >
                    <Zap size={12} />
                    {example}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {mode === 'quick' && (
          <>
            <div className={styles.content}>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={existingWorkflow ? `Add nodes to "${existingWorkflow.name}"... e.g., 'Add error handling and a Slack notification at the end'` : "Describe your workflow... e.g., 'Navigate to PromptBase, fill in the listing form, upload AI-generated images, set price to $2.99, and publish'"}
                className={styles.textarea}
                rows={4}
                disabled={generateMutation.isPending}
                autoFocus
              />
              <div className={styles.modelSelector}>
                <p className={styles.modelLabel}>AI Model:</p>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  disabled={generateMutation.isPending}
                  className={styles.modelSelect}
                >
                  {availableModels.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMode('choose')} disabled={generateMutation.isPending}>Back</Button>
              <Button onClick={handleQuickGenerate} disabled={!prompt.trim() || generateMutation.isPending}>
                {generateMutation.isPending ? (
                  <><Loader2 size={16} className={styles.spinner} /> Generating...</>
                ) : (
                  <><Send size={16} /> Generate</>
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {mode === 'interview' && (
          <>
            <div className={styles.chatArea}>
              {chatMessages.map((msg, i) => (
                <div key={i} className={`${styles.chatMsg} ${msg.role === 'user' ? styles.chatUser : styles.chatAssistant}`}>
                  <div className={styles.chatAvatar}>
                    {msg.role === 'user' ? <User size={14} /> : <Bot size={14} />}
                  </div>
                  <div className={styles.chatBubble}>{msg.content}</div>
                </div>
              ))}
              {isThinking && (
                <div className={`${styles.chatMsg} ${styles.chatAssistant}`}>
                  <div className={styles.chatAvatar}><Bot size={14} /></div>
                  <div className={styles.chatBubble}>
                    <Loader2 size={14} className={styles.spinner} /> Thinking...
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <div className={styles.chatInputArea}>
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleChatSend();
                  }
                }}
                placeholder="Describe what you want to automate..."
                className={styles.chatInput}
                disabled={isThinking}
                autoFocus
              />
              <Button size="sm" onClick={handleChatSend} disabled={!chatInput.trim() || isThinking}>
                <Send size={14} />
              </Button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMode('choose')}>Back</Button>
              {chatMessages.length >= 4 && (
                <Button
                  onClick={() => {
                    const fullPrompt = compileInterviewToPrompt(chatMessages, '');
                    handleGenerate(fullPrompt);
                  }}
                  disabled={generateMutation.isPending}
                >
                  {generateMutation.isPending ? (
                    <><Loader2 size={16} className={styles.spinner} /> Creating...</>
                  ) : (
                    <><Sparkles size={16} /> Create Workflow Now</>
                  )}
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Compile interview chat into a generation prompt
 */
function compileInterviewToPrompt(messages: ChatMessage[], planSummary: string): string {
  const userMessages = messages.filter((m) => m.role === 'user').map((m) => m.content);
  const combined = userMessages.join('\n');
  const plan = planSummary.replace(/\[WORKFLOW_READY\]|\[\/WORKFLOW_READY\]/g, '').trim();

  return `Create a detailed workflow based on this conversation:

User requirements:
${combined}

${plan ? `AI-planned workflow:\n${plan}\n` : ''}
Use the full range of available nodes: browser automation (navigate, click, type, upload, extract, wait, screenshot), AI (generate text, generate images, classify, extract data), data transforms, HTTP requests, TaskingBot delegation, installed skills when relevant, and logic (loops, conditions, delays, retry, error handling).

Make the workflow production-ready with proper error handling, waits between browser actions, realistic selectors, saved-credential usage where appropriate, and complete step details.`;
}

/**
 * Fallback interview response when API isn't available
 */
function generateInterviewResponse(messages: ChatMessage[]): string {
  const userMsgCount = messages.filter((m) => m.role === 'user').length;

  if (userMsgCount === 1) {
    return "I can work with that. One quick blocker check before I build it: should this run manually, on a schedule, or when a webhook/event fires?\n\nIf you don't care, I'll default it to manual and fill in the rest.";
  }

  if (userMsgCount === 2) {
    return `[WORKFLOW_READY]
Based on our conversation, here's the workflow I'll create:

${messages.filter((m) => m.role === 'user').map((m) => m.content).join('\n')}

I'll fill in the trigger, all required workflow details, retries/error handling, browser waits, credential usage, and TaskingBot or skill-powered steps where they fit best.
[/WORKFLOW_READY]`;
  }

  // After 3+ messages, mark as ready
  const userContent = messages.filter((m) => m.role === 'user').map((m) => m.content).join('. ');
  return `[WORKFLOW_READY]\nBased on our conversation, here's the workflow I'll create:\n\n${userContent}\n\nI'll set up browser automation for the web interactions, AI generation for content creation, proper wait times between steps, error handling with retries, and data flow between each step.\n[/WORKFLOW_READY]`;
}
